/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import MobileFormDialog from "../../components/ui/mobile-form-dialog";
import {
  Plus, Trash2, Wrench, CheckCircle2, AlertTriangle, Clock, History, Edit, ChevronDown, ChevronRight,
} from "lucide-react";
import { HHMMInput } from "../../components/ui/hhmm-input";
import { isLifeLimitName } from "../../lib/lifeLimit";
import { ComponentForm } from "../../components/ComponentsManager";


// Horas SEMPRE em H:MM (nunca decimal)
function fmtMin(m) {
  if (m == null) return "—";
  const sign = m < 0 ? "-" : "";
  const v = Math.abs(m);
  return `${sign}${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
}

function fmtDate(s) {
  if (!s) return "—";
  return s.slice(0, 10);
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// "YYYY-MM-DD" + N meses (com ajuste do dia em meses curtos) — o mesmo cálculo do servidor
function addMonthsStr(dateStr, months) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const total = (m - 1) + months;
  const ny = y + Math.floor(total / 12);
  const nm = ((total % 12) + 12) % 12;
  const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  return `${ny}-${String(nm + 1).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

// Âncora do prazo seguinte — espelho (só para pré-visualizar) da regra do servidor, services/di_tolerance.py
function planAnchor(scheduled, actual, tol, isDate) {
  if (scheduled == null) return { anchor: actual, why: "first" };
  const low = isDate ? addMonthsStr(scheduled, -tol) : scheduled - tol;
  const high = isDate ? addMonthsStr(scheduled, tol) : scheduled + tol;
  if (actual < low) return { anchor: actual, why: "early" };
  if (actual > high) return { anchor: actual, why: "late" };
  return { anchor: scheduled, why: "scheduled" };
}

// 180 meses → "15 anos"; 18 meses → "18 meses"
function monthsText(m) {
  if (!m) return "";
  return m % 12 === 0 ? `${m / 12} ${m / 12 === 1 ? "ano" : "anos"}` : `${m} meses`;
}

// Tarefas que a execução de `item` pode cobrir: do mesmo componente, com intervalo em horas MENOR (a inspeção das
// 100 h inclui as de 25 h e 50 h). O servidor repete estas regras; só se fecha o que o utilizador marcar.
function includedCandidates(item, all) {
  if (!item || item.rule === "potential" || !(item.interval_hours_min > 0)) return [];
  const sameComponent = (o) => ((item.component_id || o.component_id)
    ? item.component_id === o.component_id
    : (item.component || "").trim().toLowerCase() === (o.component || "").trim().toLowerCase());
  return (all || [])
    .filter((o) => o.id !== item.id && o.rule !== "potential" && o.interval_hours_min > 0
      && o.interval_hours_min < item.interval_hours_min && sameComponent(o))
    .sort((a, b) => b.interval_hours_min - a.interval_hours_min);
}
const isInspection = (o) => /inspe[cç]/i.test(o.task || "");

function severityChip(sev, configured, hasTriggers, state, rule, noTolerance) {
  if (state === "out_of_tolerance") {
    const potential = rule === "potential";
    const title = potential ? "Potencial do motor esgotado: a aeronave fica não operacional"
      : noTolerance ? "Vencida e sem tolerância: a aeronave fica não operacional"
      : "Fora da tolerância: a aeronave fica não operacional";
    const label = potential ? "Potencial esgotado" : noTolerance ? "Vencida · sem tolerância" : "Fora de tolerância";
    return <span className="inline-flex items-center gap-1 text-red-300 bg-red-500/15 px-2 py-0.5 rounded text-[10px] font-bold uppercase" title={title}><AlertTriangle className="w-3 h-3" /> {label}</span>;
  }
  if (state === "in_tolerance") {
    const potential = rule === "potential";
    return <span className="inline-flex items-center gap-1 text-signal-300 bg-signal-500/15 px-2 py-0.5 rounded text-[10px] font-bold uppercase" title={potential ? "Potencial atingido, a operar na extensão: a aeronave continua operacional, com aviso" : "Vencida, mas dentro da tolerância: a aeronave continua operacional, com aviso"}><Clock className="w-3 h-3" /> {potential ? "Em extensão do potencial" : "Vencida · em tolerância"}</span>;
  }
  if (sev === "critical") {
    return <span className="inline-flex items-center gap-1 text-red-300 bg-red-500/15 px-2 py-0.5 rounded text-[10px] font-bold uppercase"><AlertTriangle className="w-3 h-3" /> Crítico</span>;
  }
  if (sev === "warn") {
    return <span className="inline-flex items-center gap-1 text-signal-300 bg-signal-500/15 px-2 py-0.5 rounded text-[10px] font-bold uppercase"><Clock className="w-3 h-3" /> Atenção</span>;
  }
  if (sev === "info") {
    return <span className="inline-flex items-center gap-1 text-amber-300 bg-amber-500/15 px-2 py-0.5 rounded text-[10px] uppercase"><Clock className="w-3 h-3" /> Em breve</span>;
  }
  if (!hasTriggers) {
    return <span className="inline-flex items-center gap-1 text-blue-300 bg-blue-500/15 px-2 py-0.5 rounded text-[10px] uppercase" title="Sem periodicidade — registo livre de execuções">
      <History className="w-3 h-3" /> Registo livre
    </span>;
  }
  if (!configured) {
    return <span className="inline-flex items-center gap-1 text-slate-400 bg-slate-700/30 px-2 py-0.5 rounded text-[10px] uppercase">Por configurar</span>;
  }
  return <span className="inline-flex items-center gap-1 text-emerald-300 bg-emerald-500/15 px-2 py-0.5 rounded text-[10px] uppercase"><CheckCircle2 className="w-3 h-3" /> Em dia</span>;
}


/** Pede um motivo antes de corrigir/apagar (fica registado, com o nome de quem o fez). */
function ReasonDialog({ title, description, confirmLabel, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 3;
  const go = async () => {
    if (!ok || busy) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-md" data-testid="di-maint-reason-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">{description}</DialogDescription>
        </DialogHeader>
        <div className="text-xs">
          <Label className="text-slate-300">Motivo * <span className="text-slate-500 font-normal">(fica registado no histórico de alterações)</span></Label>
          <Input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") go(); }}
            placeholder="ex.: registado por engano"
            className="bg-slate-800 border-slate-700 mt-1"
            data-testid="di-maint-reason-input"
          />
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onCancel} className="border-slate-600 text-slate-300">Cancelar</Button>
          <Button onClick={go} disabled={!ok || busy} className="bg-red-600 hover:bg-red-500 text-white" data-testid="di-maint-reason-confirm">
            {busy ? "A guardar…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function MaintenanceDialog({ aircraftId, registration, onClose }) {
  const [data, setData] = useState({ items: [], current_hours_min: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [editingEngine, setEditingEngine] = useState(null);   // o componente Motor, a editar o potencial

  // A tarefa «Substituição do motor» é gerada pelo motor: edita-se no próprio componente
  const editEngine = async (item) => {
    try {
      const r = await axios.get(`${API}/di/aircraft/${aircraftId}/components`, { withCredentials: true });
      const comp = (r.data || []).find((c) => c.id === item.component_id);
      if (!comp) { toast.error("Motor não encontrado nos componentes da aeronave."); return; }
      setEditingEngine(comp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a abrir o motor");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/di/aircraft/${aircraftId}/maintenance`, { withCredentials: true });
      setData(r.data || { items: [], current_hours_min: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar manutenções");
    } finally {
      setLoading(false);
    }
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (item, reason) => {
    try {
      await axios.delete(`${API}/di/maintenance/${item.id}`, { withCredentials: true, params: { reason } });
      toast.success("Item arquivado e removido");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Erro");
    }
  };

  // Sort items by urgency (fora de tolerância first, then severity, then days/minutes remaining)
  function _urgencyScore(it) {
    const st = it.status || {};
    const sevRank = { critical: 0, warn: 1, info: 2 };
    const tier = st.severity != null ? sevRank[st.severity] : (st.configured ? 3 : 4);
    // Secondary: smallest remaining time wins
    const daysScore = st.days_remaining != null ? st.days_remaining : 999_999;
    const hoursScore = st.minutes_remaining != null ? Math.floor(st.minutes_remaining / 30) : 999_999;
    const remaining = Math.min(daysScore, hoursScore);
    return [tier, remaining];
  }
  function _cmpItems(a, b) {
    const sa = _urgencyScore(a);
    const sb = _urgencyScore(b);
    if (sa[0] !== sb[0]) return sa[0] - sb[0];
    return sa[1] - sb[1];
  }

  // Group items by component
  const grouped = {};
  (data.items || []).forEach((it) => {
    const key = it.component || "—";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(it);
  });
  Object.values(grouped).forEach((arr) => arr.sort(_cmpItems));
  // Sort components by their most urgent item
  const components = Object.keys(grouped).sort((a, b) => {
    const sa = _urgencyScore(grouped[a][0]);
    const sb = _urgencyScore(grouped[b][0]);
    if (sa[0] !== sb[0]) return sa[0] - sb[0];
    if (sa[1] !== sb[1]) return sa[1] - sb[1];
    return a.localeCompare(b);
  });

  const blockedCount = (data.items || []).filter((it) => it.status?.blocked).length;
  const warningCount = (data.items || []).filter((it) => it.status?.warning).length;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="di-maint-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-300" /> Manutenções — {registration}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Cria componentes (hélice, motor, célula, …) e tarefas com prazos por horas e/ou calendário (vale o que ocorrer primeiro) —
            ou deixa <span className="text-blue-300">sem periodicidade</span> e usa como registo livre de execuções.
            Relógio do motor (voo + solo): <span className="text-amber-300 font-mono" data-testid="di-maint-current-hours">{fmtMin(data.current_hours_min)}</span>
          </DialogDescription>
        </DialogHeader>

        {blockedCount > 0 && (
          <div className="rounded border border-red-500/40 bg-red-500/10 text-red-200 text-xs px-3 py-2" data-testid="di-maint-blocked-banner">
            <strong>Aeronave não operacional:</strong> {blockedCount} {blockedCount === 1 ? "manutenção está" : "manutenções estão"} fora da tolerância.
          </div>
        )}
        {blockedCount === 0 && warningCount > 0 && (
          <div className="rounded border border-signal-500/40 bg-signal-500/10 text-signal-200 text-xs px-3 py-2" data-testid="di-maint-warning-banner">
            <strong>Operacional, com aviso:</strong> {warningCount} {warningCount === 1 ? "manutenção vencida está" : "manutenções vencidas estão"} dentro da tolerância — regista-{warningCount === 1 ? "a" : "as"} o quanto antes.
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 mb-3">
          <Button size="sm" onClick={() => { setEditing(null); setCreating(true); }} className="btn-cockpit" data-testid="di-maint-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Novo item
          </Button>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm">A carregar…</div>
        ) : components.length === 0 ? (
          <div className="text-slate-400 text-sm italic" data-testid="di-maint-empty">
            Sem itens de manutenção. Crie um para começar (ex: Hélice · Inspeção 100h).
          </div>
        ) : (
          <div className="space-y-4" data-testid="di-maint-list">
            {components.map((comp) => (
              <ComponentGroup
                key={comp}
                component={comp}
                items={grouped[comp]}
                currentHoursMin={data.current_hours_min}
                onEdit={(it) => { setCreating(false); setEditing(it); }}
                onDelete={(it) => setDeleting(it)}
                onComplete={(it) => setCompleting(it)}
                onHistory={(it) => setHistoryOf(it)}
                onEditEngine={editEngine}
              />
            ))}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Fechar</Button>
        </DialogFooter>

        {(creating || editing) && (
          <MaintenanceItemForm
            aircraftId={aircraftId}
            item={editing}
            currentHoursMin={data.current_hours_min}
            engineManaged={(data.items || []).some((i) => i.system)}
            onClose={() => { setCreating(false); setEditing(null); }}
            onSaved={() => { setCreating(false); setEditing(null); load(); }}
          />
        )}

        {editingEngine && (
          <ComponentForm
            open
            aircraftId={aircraftId}
            initial={editingEngine}
            onClose={() => setEditingEngine(null)}
            onSaved={() => { setEditingEngine(null); load(); }}
          />
        )}

        {completing && (
          <CompleteForm
            item={completing}
            items={data.items}
            currentHoursMin={data.current_hours_min}
            onClose={() => setCompleting(null)}
            onSaved={() => { setCompleting(null); load(); }}
          />
        )}

        {historyOf && (
          <HistoryDialog
            item={historyOf}
            onClose={() => setHistoryOf(null)}
            onChanged={load}
          />
        )}

        {deleting && (
          <ReasonDialog
            title={`Eliminar "${deleting.component} · ${deleting.task}"?`}
            description="O item e o seu histórico saem do controlo ativo, mas ficam arquivados (com o motivo e o seu nome)."
            confirmLabel="Eliminar"
            onCancel={() => setDeleting(null)}
            onConfirm={(reason) => remove(deleting, reason)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


function ComponentGroup({ component, items, currentHoursMin, onEdit, onDelete, onComplete, onHistory, onEditEngine }) {
  const [open, setOpen] = useState(true);
  // Compute worst severity for group header chip
  const rank = { critical: 3, warn: 2, info: 1 };
  let worst = null;
  let worstState = null;
  let worstRule = null;
  let worstNoTol = false;
  items.forEach((it) => {
    const s = it.status?.severity;
    if (s && (!worst || rank[s] > rank[worst])) {
      worst = s;
      worstState = it.status?.state || null;
      worstRule = it.status?.rule || null;
      worstNoTol = !!it.status?.no_tolerance;
    }
  });
  const headerCls = worst === "critical"
    ? "border-red-500/30 bg-red-500/5"
    : worst === "warn"
    ? "border-signal-500/30 bg-signal-500/5"
    : worst === "info"
    ? "border-amber-500/30 bg-amber-500/5"
    : "border-slate-700 bg-slate-800/30";

  return (
    <div className={`border rounded ${headerCls}`} data-testid="di-maint-component-group">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-800/30"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <Wrench className="w-4 h-4 text-amber-300" />
          <span className="font-semibold text-slate-100">{component}</span>
          <span className="text-[10px] text-slate-400">· {items.length} {items.length === 1 ? "tarefa" : "tarefas"}</span>
        </div>
        {worst && severityChip(worst, true, true, worstState, worstRule, worstNoTol)}
      </button>
      {open && (
        <div className="p-3 pt-0 space-y-2">
          {items.map((it) => (
            <MaintenanceItemCard
              key={it.id}
              item={it}
              currentHoursMin={currentHoursMin}
              onEdit={() => onEdit(it)}
              onDelete={() => onDelete(it)}
              onComplete={() => onComplete(it)}
              onHistory={() => onHistory(it)}
              onEditEngine={() => onEditEngine(it)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// Documentos de referência (só para quem tem sessão): o servidor entrega os PDFs. API lê-se dentro da função de propósito.
function ReferenceDocLinks({ className = "" }) {
  const base = `${API}/di/reference-docs`;
  return (
    <span className={className} data-testid="di-maint-reference-docs">
      Documentos:{" "}
      <a href={`${base}/rotax-mml-912-tbo`} target="_blank" rel="noreferrer" className="underline text-sky-300 hover:text-sky-200">manual Rotax (MML-912, 05-10-00)</a>
      {" · "}
      <a href={`${base}/cti-05-04`} target="_blank" rel="noreferrer" className="underline text-sky-300 hover:text-sky-200">CTI 05-04 do INAC</a>
    </span>
  );
}

// O que a CTI 05-04 exige para operar além do potencial (resumo; confirmar com a organização de manutenção)
function PotentialConditions({ open, source }) {
  const fromCti = source === "cti";
  return (
    <details open={fromCti && open} className="mt-1 text-[11px] text-slate-400" data-testid="di-maint-cti-conditions">
      <summary className="cursor-pointer text-slate-300">
        {fromCti ? "Condições para operar além do potencial (CTI 05-04)" : "CTI 05-04: condições (quando a excedência vem da CTI)"}
      </summary>
      {!fromCti && (
        <p className="mt-1" data-testid="di-maint-cti-not-applied">
          Dentro da excedência autorizada pelo fabricante, o entendimento é que estas condições não se aplicam (CTI, ponto 4.1:
          «salvo indicação contrária»). Confirme com a sua organização de manutenção. Aplicam-se se a excedência vier da CTI.
        </p>
      )}
      <ul className="list-disc pl-4 mt-1 space-y-0.5">
        <li>A <strong>inspeção especial</strong> (relatório técnico anexo à CTI, ponto 4.5) tem de ser feita <strong>antes</strong> de exceder o potencial, por pessoa ou organização habilitada, e ficar registada na caderneta do motor.</li>
        <li>Depois, inspeções de <strong>100 horas ou anuais</strong>, o que ocorrer primeiro.</li>
        <li>O motor deve ter operado pelo menos <strong>200 horas</strong> em aeronave de matrícula portuguesa antes de completar o período de revisão geral do fabricante.</li>
        <li>Diretivas de navegabilidade exigidas na revisão geral cumpridas (salvo acordo do INAC).</li>
        <li>Se houver um problema durante a extensão, a manutenção notifica o INAC de imediato; se a inspeção rejeitar o motor, tem de ir a revisão geral antes de voltar ao serviço.</li>
        <li>Os prazos do programa de manutenção e as ações mandatórias continuam a aplicar-se (ponto 4.6).</li>
        <li>Ponto 4.3 (calendário atingido primeiro: pode continuar até ao limite de horas, só em aeronave que não faça transporte aéreo, trabalho aéreo, acrobacia, reboque ou paraquedismo): a plataforma <strong>não</strong> o aplica; o fim da extensão é o do calendário.</li>
      </ul>
      <p className="mt-1">Vale sempre o que o fabricante diz; se não disser nada, a CTI. Confirme com a sua organização de manutenção o que se aplica ao seu caso. <ReferenceDocLinks /></p>
    </details>
  );
}


// «até 2100:00 de motor (faltam 44:01) ou até 24/10/2026 (faltam 17 dias)»: o limite para substituir o motor
function replaceByParts(rb) {
  const parts = [];
  if (rb?.hours_min != null) {
    parts.push(<span key="h">até <strong>{fmtMin(rb.hours_min)}</strong> de motor{rb.minutes_left != null && rb.minutes_left >= 0 ? <> (faltam <strong>{fmtMin(rb.minutes_left)}</strong>)</> : null}</span>);
  }
  if (rb?.date) {
    parts.push(<span key="d">até <strong>{fmtDate(rb.date)}</strong>{rb.days_left != null && rb.days_left >= 0 ? <> (faltam <strong>{rb.days_left} dias</strong>)</> : null}</span>);
  }
  return parts.reduce((acc, el, i) => (i ? [...acc, " ou ", el] : [el]), []);
}


function MaintenanceItemCard({ item, currentHoursMin, onEdit, onDelete, onComplete, onHistory, onEditEngine }) {
  const st = item.status || {};
  const isSys = !!item.system;                 // «Substituição do motor»: gerada pelo componente Motor
  const rb = st.replace_by || {};
  const [notesOpen, setNotesOpen] = useState(false);
  const notesHasContent = !!(item.notes && item.notes.trim());
  const notesIsLong = notesHasContent && (item.notes.split("\n").length > 2 || item.notes.length > 120);
  const dueCls = (state) => (state === "out_of_tolerance" ? "text-red-300" : state === "in_tolerance" ? "text-signal-300" : "text-emerald-300");
  const anchorDiffers = item.anchor_hours_min != null && item.last_done_hours_min != null && item.anchor_hours_min !== item.last_done_hours_min;
  const isPot = st.rule === "potential";
  const ext = st.extension || {};
  return (
    <div className="border border-slate-700 rounded p-3 bg-slate-900/50" data-testid="di-maint-item-card">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h5 className="font-semibold text-slate-100" data-testid="di-maint-item-task">{item.task}</h5>
            {severityChip(st.severity, st.configured, st.has_calendar_trigger || st.has_hours_trigger || isSys, st.state, st.rule, st.no_tolerance)}
            {isPot && <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-slate-600 rounded px-1.5 py-0.5" title="Potencial do motor (TBO do fabricante) com a excedência autorizada">Potencial</span>}
            {isSys && (
              <span className="text-[10px] uppercase tracking-wide text-sky-300 border border-sky-500/40 rounded px-1.5 py-0.5" data-testid="di-maint-system-chip"
                title="Esta tarefa é obrigatória e é gerada pelo componente Motor: o potencial altera-se no próprio motor.">
                Obrigatória · gerada pelo motor
              </span>
            )}
            {st.life_limit && !isPot && (
              <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-slate-600 rounded px-1.5 py-0.5" data-testid="di-maint-life-limit"
                title="Limite de vida (substituição, overhaul, revisão geral): não tem tolerância. Para o motor, escolha «Potencial do motor» ao editar o item: o sistema aplica a excedência que o fabricante autoriza (ou a da CTI 05-04).">
                Sem tolerância
              </span>
            )}
          </div>
          {isPot && (
            <div className="mt-1 text-[11px] text-slate-300 space-y-0.5" data-testid="di-maint-potential">
              <div>
                <span className="text-slate-500">Potencial:</span>{" "}
                {item.interval_hours_min > 0 || item.interval_months > 0 ? (
                  <>
                    {[item.interval_hours_min > 0 ? `${fmtMin(item.interval_hours_min)} h` : null, item.interval_months > 0 ? monthsText(item.interval_months) : null].filter(Boolean).join(" ou ")}
                    <span className="text-slate-500"> (o que ocorrer primeiro)</span>
                  </>
                ) : (
                  <span className="text-amber-300" data-testid="di-maint-potential-missing">por definir</span>
                )}
              </div>
              {(rb.hours_min != null || rb.date) && (
                <div
                  className={`rounded border px-2 py-1.5 my-1 text-[12px] ${st.state === "out_of_tolerance" ? "border-red-500/40 bg-red-500/10 text-red-200" : st.state === "in_tolerance" || st.state === "approaching" ? "border-amber-500/40 bg-amber-500/10 text-amber-100" : "border-emerald-500/30 bg-emerald-500/5 text-emerald-100"}`}
                  data-testid="di-maint-replace-by"
                >
                  {st.state === "out_of_tolerance"
                    ? <><strong>Prazo de substituição do motor terminado.</strong> A aeronave não pode voar até o motor ser substituído.</>
                    : <><strong>O motor tem de ser substituído</strong> {rb.exact ? "" : "(o mais tardar) "}{replaceByParts(rb)}, o que ocorrer primeiro.</>}
                  {!rb.exact && st.state !== "out_of_tolerance" && (
                    <div className="text-[10px] opacity-80 mt-0.5">O potencial ainda não foi atingido: este é o limite máximo e só pode adiantar-se se o potencial for atingido primeiro por um dos lados (horas ou anos).</div>
                  )}
                </div>
              )}
              {!ext.reached && st.next_due_hours_min != null && st.minutes_remaining != null && (
                <div>Horas: faltam <span className="font-mono text-emerald-300">{fmtMin(st.minutes_remaining)}</span> de motor (potencial às {fmtMin(st.next_due_hours_min)})</div>
              )}
              {!ext.reached && st.next_due_date && st.days_remaining != null && (
                <div>Calendário: o potencial termina em <span className="font-mono text-emerald-300">{st.days_remaining}d</span> ({fmtDate(st.next_due_date)})</div>
              )}
              {ext.reached && (
                <div className={st.state === "out_of_tolerance" ? "text-red-300" : "text-signal-300"} data-testid="di-maint-extension">
                  Potencial atingido a {fmtDate(ext.reached_on)} ({ext.reached_by === "hours" ? "pelas horas" : "pelo calendário"}
                  {ext.reached_on_source === "logbook" && <>; data da caderneta do motor{ext.reached_note ? `, ${ext.reached_note}` : ""}{ext.reached_on_flights && ext.reached_on_flights !== ext.reached_on ? `; os voos registados dariam ${fmtDate(ext.reached_on_flights)}` : ""}</>}).
                  {" "}Excedência {ext.source === "cti" ? "da CTI 05-04, porque o fabricante não indica" : "autorizada pelo fabricante"}
                  {" "}({[ext.hours_min > 0 ? `${ext.hours_pct} % das horas` : null, ext.months != null ? `${ext.months} meses` : null].filter(Boolean).join(" ou ")}, o que ocorrer primeiro
                  {ext.limited_by_cti ? "; limitada ao teto da CTI 05-04" : ""}):{" "}
                  {st.state === "out_of_tolerance"
                    ? "terminou."
                    : <>termina em {[
                        ext.end_date != null ? <strong key="d">{ext.days_left} dias ({fmtDate(ext.end_date)})</strong> : null,
                        ext.end_hours_min != null ? <span key="h"><strong>{fmtMin(ext.minutes_left)}</strong> de motor (às {fmtMin(ext.end_hours_min)})</span> : null,
                      ].filter(Boolean).reduce((acc, el, i) => (i ? [...acc, " ou em ", el] : [el]), [])}, o que ocorrer primeiro.</>}
                </div>
              )}
              <PotentialConditions open={ext.reached || st.state === "approaching"} source={ext.source} />
            </div>
          )}
          {!isPot && (
          <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
            {st.has_calendar_trigger && (
              <div className="text-slate-300">
                <span className="text-slate-500">Calendário:</span> a cada {item.interval_months} {item.interval_months === 1 ? "mês" : "meses"}
                {st.next_due_date && (
                  <span className={`ml-1 font-mono ${dueCls(st.calendar_state)}`}>
                    · próx: {fmtDate(st.next_due_date)}
                    {st.days_remaining != null && (
                      <span className="ml-1 text-slate-500">
                        ({st.days_remaining >= 0 ? `${st.days_remaining}d` : `${-st.days_remaining}d em atraso`}
                        {st.calendar_state === "in_tolerance" || st.calendar_state === "out_of_tolerance" ? ` · tolerância até ${fmtDate(st.limit_date)}` : ""})
                      </span>
                    )}
                  </span>
                )}
              </div>
            )}
            {st.has_hours_trigger && (
              <div className="text-slate-300">
                <span className="text-slate-500">Horas:</span> a cada {fmtMin(item.interval_hours_min)}
                {st.next_due_hours_min != null && (
                  <span className={`ml-1 font-mono ${dueCls(st.hours_state)}`}>
                    · próx: {fmtMin(st.next_due_hours_min)}
                    {st.minutes_remaining != null && (
                      <span className="ml-1 text-slate-500">
                        ({st.minutes_remaining >= 0 ? `${fmtMin(st.minutes_remaining)} restantes` : `${fmtMin(-st.minutes_remaining)} em atraso`}
                        {st.hours_state === "in_tolerance" || st.hours_state === "out_of_tolerance" ? ` · tolerância até ${fmtMin(st.limit_hours_min)}` : ""})
                      </span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
          )}
          {!isPot && item.beyond_engine_limit && (
            <div className="mt-1 text-[11px] text-signal-300" data-testid="di-maint-beyond-engine">
              <strong>Não faz sentido contar com esta tarefa:</strong> o motor tem de ser substituído às{" "}
              <span className="font-mono">{fmtMin(item.beyond_engine_limit.hours_min)}</span> de motor
              {item.beyond_engine_limit.exact ? "" : " (o mais tardar)"}
              {item.beyond_engine_limit.date ? <> ({fmtDate(item.beyond_engine_limit.date)})</> : null}, antes desta tarefa
              chegar às <span className="font-mono">{fmtMin(st.next_due_hours_min)}</span>. Fica aqui para registo.
            </div>
          )}
          {st.state === "out_of_tolerance" && (
            <div className="mt-1 text-[11px] text-red-300" data-testid="di-maint-blocked-note">
              {isPot
                ? <>Potencial do motor esgotado — a aeronave fica <strong>não operacional</strong> até o motor ser revisto ou substituído e isso ser registado.</>
                : st.no_tolerance
                ? <>Vencida e sem tolerância — a aeronave fica <strong>não operacional</strong> até esta manutenção ser registada.</>
                : <>Fora da tolerância — a aeronave fica <strong>não operacional</strong> até esta manutenção ser registada.</>}
            </div>
          )}
          {st.state === "in_tolerance" && (
            <div className="mt-1 text-[11px] text-signal-300" data-testid="di-maint-warning-note">
              {isPot
                ? <>Em extensão do potencial — a aeronave continua operacional, <strong>com aviso</strong>. Planeie já a revisão geral ou a substituição.</>
                : <>Vencida, mas dentro da tolerância — a aeronave continua operacional, <strong>com aviso</strong>.</>}
            </div>
          )}
          {item.last_done_at && (
            <div className="mt-1 text-[10px] text-slate-500">
              {isPot ? "Início do potencial" : "Última execução"}: {fmtDate(item.last_done_at)} · {fmtMin(item.last_done_hours_min)}
              {anchorDiffers && !isPot && <span> — o prazo seguinte conta a partir do prazo original ({fmtMin(item.anchor_hours_min)})</span>}
            </div>
          )}
          {(st.issues || []).map((issue, i) => (
            <div key={i} className="mt-1 text-[11px] text-amber-300 flex items-start gap-1" data-testid="di-maint-issue">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> <span>{issue}</span>
            </div>
          ))}
          {!st.configured && (st.has_calendar_trigger || st.has_hours_trigger) && (
            <div className="mt-1 text-[10px] text-amber-400 italic">
              Por configurar — registe a 1ª execução para começar a calcular o próximo limite.
            </div>
          )}
          {!st.has_calendar_trigger && !st.has_hours_trigger && !isSys && (
            <div className="mt-1 text-[10px] text-blue-300/80 italic">
              Sem periodicidade — usa o botão <span className="font-semibold">Registar manutenção</span> para adicionar entradas ao histórico.
            </div>
          )}
          {notesHasContent && (
            <div className="mt-1.5 text-[11px]">
              {notesIsLong && !notesOpen ? (
                <button
                  onClick={() => setNotesOpen(true)}
                  className="flex items-center gap-1 text-amber-300 hover:text-amber-200"
                  data-testid="di-maint-notes-expand"
                >
                  <ChevronDown className="w-3 h-3" />
                  Ver checklist ({item.notes.split("\n").filter((l) => l.trim()).length} {item.notes.split("\n").filter((l) => l.trim()).length === 1 ? "linha" : "linhas"})
                </button>
              ) : (
                <div>
                  {notesIsLong && (
                    <button
                      onClick={() => setNotesOpen(false)}
                      className="flex items-center gap-1 text-slate-400 hover:text-slate-300 mb-1"
                      data-testid="di-maint-notes-collapse"
                    >
                      <ChevronRight className="w-3 h-3 rotate-90" /> Esconder
                    </button>
                  )}
                  <pre
                    className="whitespace-pre-wrap font-mono text-[11px] text-slate-300 bg-slate-950/50 border border-slate-800 rounded p-2"
                    data-testid="di-maint-notes-content"
                  >
                    {item.notes}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1 sm:shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800">
          {isSys ? (
            <button
              onClick={onEditEngine}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 text-[11px] font-semibold border border-sky-500/30"
              title="O potencial altera-se no próprio motor (componente)"
              data-testid="di-maint-edit-engine-btn"
            >
              <Edit className="w-3.5 h-3.5" />
              <span>Editar no motor</span>
            </button>
          ) : (
            <>
              <button
                onClick={onComplete}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-[11px] font-semibold border border-emerald-500/30"
                title="Registar uma execução desta manutenção"
                data-testid="di-maint-complete-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Registar manutenção</span>
              </button>
              <button onClick={onHistory} className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800 text-slate-300 hover:text-blue-300 text-[11px] border border-slate-700" title="Ver histórico de execuções" data-testid="di-maint-history-btn">
                <History className="w-3.5 h-3.5" />
                <span>Histórico</span>
              </button>
              <button onClick={onEdit} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-orange-300" title="Editar" data-testid="di-maint-edit-btn">
                <Edit className="w-3.5 h-3.5" />
              </button>
              <button onClick={onDelete} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400" title="Eliminar" data-testid="di-maint-delete-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function MaintenanceItemForm({ aircraftId, item, currentHoursMin, engineManaged, onClose, onSaved }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState(() => ({
    component: item?.component || "",
    component_id: item?.component_id || "",
    task: item?.task || "",
    interval_hours_min: item?.interval_hours_min || 0,
    interval_months: item?.interval_months || 0,
    tolerance_hours_min: item?.tolerance_hours_min ?? null,
    tolerance_months: item?.tolerance_months ?? null,
    rule: item?.rule === "potential" ? "potential" : "tolerance",
    extension_source: item?.extension_source === "cti" ? "cti" : "manufacturer",
    extension_hours_pct: item?.extension_hours_pct ?? null,
    extension_months: item?.extension_months ?? null,
    potential_reached_on: item?.potential_reached_on || "",
    potential_reached_note: item?.potential_reached_note || "",
    last_done_at: item?.last_done_at || "",
    last_done_hours_min: item?.last_done_hours_min ?? null,
    notes: item?.notes || "",
  }));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [components, setComponents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/di/aircraft/${aircraftId}/components`, { withCredentials: true });
        if (!cancelled) setComponents(r.data || []);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [aircraftId]);

  // Mudar prazos, intervalos, tolerâncias ou a última execução exige motivo (fica na auditoria)
  const num = (v) => (v ? Number(v) : null);
  const criticalChanged = isEdit && (
    num(form.interval_hours_min) !== num(item.interval_hours_min)
    || num(form.interval_months) !== num(item.interval_months)
    || (form.last_done_at || null) !== (item.last_done_at || null)
    || (form.last_done_hours_min ?? null) !== (item.last_done_hours_min ?? null)
    || (form.component_id || null) !== (item.component_id || null)
    || (form.tolerance_hours_min ?? null) !== (item.tolerance_hours_min ?? null)
    || (form.tolerance_months ?? null) !== (item.tolerance_months ?? null)
    || (form.rule === "potential") !== (item.rule === "potential")
    || (form.potential_reached_on || null) !== (item.potential_reached_on || null)
    || (form.extension_source || "manufacturer") !== (item.extension_source || "manufacturer")
    || (form.extension_hours_pct ?? null) !== (item.extension_hours_pct ?? null)
    || (form.extension_months ?? null) !== (item.extension_months ?? null)
  );
  const isPot = form.rule === "potential";
  // Limite de vida (substituição do motor, overhaul...): o servidor aplica 0 de tolerância se nada estiver escrito
  const lifeDefault = isLifeLimitName(form.task);
  const tolUnset = form.tolerance_hours_min == null && form.tolerance_months == null;

  const submit = async () => {
    setError("");
    if (!form.component.trim() || !form.task.trim()) {
      setError("Componente e tarefa são obrigatórios.");
      return;
    }
    if (form.last_done_at && form.last_done_at > todayStr()) {
      setError("A data da última execução não pode estar no futuro.");
      return;
    }
    if (criticalChanged && reason.trim().length < 3) {
      setError("Indique o motivo da alteração (fica registado).");
      return;
    }
    if (isPot && !Number(form.interval_hours_min || 0) && !Number(form.interval_months || 0)) {
      setError("O potencial do motor precisa de horas e/ou meses (15 anos = 180 meses).");
      return;
    }
    const intH = Number(form.interval_hours_min || 0);
    const intM = Number(form.interval_months || 0);
    // Intervalos opcionais — sem nenhum, vira "registo livre"
    setSaving(true);
    try {
      const payload = {
        component: form.component.trim(),
        component_id: form.component_id || null,
        task: form.task.trim(),
        interval_hours_min: intH || null,
        interval_months: intM || null,
        tolerance_hours_min: isPot ? null : (form.tolerance_hours_min ?? null),
        tolerance_months: isPot ? null : (form.tolerance_months ?? null),
        rule: isPot ? "potential" : null,
        potential_reached_on: isPot ? (form.potential_reached_on || null) : null,
        potential_reached_note: isPot ? ((form.potential_reached_note || "").trim() || null) : null,
        extension_source: isPot ? form.extension_source : null,
        extension_hours_pct: isPot && form.extension_source !== "cti" ? (form.extension_hours_pct ?? null) : null,
        extension_months: isPot && form.extension_source !== "cti" ? (form.extension_months ?? null) : null,
        last_done_at: form.last_done_at || null,
        last_done_hours_min: form.last_done_hours_min ?? null,
        notes: form.notes || "",
      };
      if (isEdit) {
        if (criticalChanged) payload.reason = reason.trim();
        await axios.put(`${API}/di/maintenance/${item.id}`, payload, { withCredentials: true });
        toast.success("Item atualizado");
      } else {
        await axios.post(`${API}/di/aircraft/${aircraftId}/maintenance`, payload, { withCredentials: true });
        toast.success("Item criado");
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileFormDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Editar ${item.component} · ${item.task}` : "Nova manutenção"}
      description="Define intervalos por horas e/ou meses para que o sistema avise automaticamente quando o limite estiver a chegar. Deixa ambos a zero se só quiseres usar isto como registo livre de execuções (ex: manutenções pontuais sem periodicidade fixa)."
      size="md"
      onSubmit={submit}
      submitLabel={isEdit ? "Guardar" : "Criar"}
      submitting={saving}
      testId="di-maint-form-dialog"
    >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <Label className="text-slate-300">Componente *</Label>
            <Input
              value={form.component}
              onChange={(e) => setForm({ ...form, component: e.target.value })}
              placeholder="ex.: Hélice / Motor / Célula"
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-component"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-300">Ligar a componente físico (opcional)</Label>
            <select
              value={form.component_id || ""}
              onChange={(e) => setForm({ ...form, component_id: e.target.value })}
              className="bg-slate-800 border-slate-700 rounded h-9 px-2 text-sm text-slate-100 w-full mt-1"
              data-testid="di-maint-form-component-id"
            >
              <option value="">— Relógio do motor (predefinição)</option>
              {components.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.kind.toUpperCase()} · {[c.manufacturer, c.model].filter(Boolean).join(" ")} {c.serial_number ? `(SN ${c.serial_number})` : ""} — {fmtMin(c.current_hours_min)}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-slate-500 mt-0.5">
              Quando ligado, as horas-limite contam a partir do contador deste componente
              (útil para "Revisão 100h do motor" — não conta as horas da célula).
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-300">Tipo de prazo</Label>
            <select
              value={form.rule}
              onChange={(e) => setForm({ ...form, rule: e.target.value })}
              className="bg-slate-800 border-slate-700 rounded h-9 px-2 text-sm text-slate-100 w-full mt-1"
              data-testid="di-maint-form-rule"
            >
              <option value="tolerance">Normal (tolerância ANAC depois do prazo)</option>
              <option value="potential" disabled={!!engineManaged && item?.rule !== "potential"}>
                Potencial do motor (TBO do fabricante + excedência){engineManaged && item?.rule !== "potential" ? " — já definido no motor" : ""}
              </option>
            </select>
            {isPot && (
              <div className="text-[10px] text-slate-500 mt-0.5" data-testid="di-maint-form-rule-help">
                O motor não tem a tolerância normal: tem um potencial (TBO) que o fabricante fixa (por exemplo, Rotax 912 ULS
                de S/N 6775790 a 6787000: 2000 h ou 15 anos, o que ocorrer primeiro) e, depois de o atingir, uma excedência
                contada a partir de quando o potencial foi atingido. Indique só o potencial, sem somar a excedência: o
                sistema aplica-a e descobre pelos voos quando o potencial foi atingido. Vale sempre o que o fabricante diz;
                se não disser nada, aplica-se a CTI 05-04 do INAC (que é também o teto). <ReferenceDocLinks />
              </div>
            )}
          </div>
          <div>
            <Label className="text-slate-300">Tarefa *</Label>
            <Input
              value={form.task}
              onChange={(e) => setForm({ ...form, task: e.target.value })}
              placeholder="ex.: Inspeção 100h"
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-task"
            />
          </div>
          <div>
            <Label className="text-slate-300">{isPot ? "Potencial em horas (HH:MM)" : "Intervalo horas (HH:MM)"}</Label>
            <HHMMInput
              value={form.interval_hours_min || 0}
              onChange={(v) => setForm({ ...form, interval_hours_min: v })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-interval-hours"
            />
          </div>
          <div>
            <Label className="text-slate-300">{isPot ? "Potencial em meses (15 anos = 180)" : "Intervalo meses"}</Label>
            <Input
              type="number" min={0} max={1200}
              value={form.interval_months || ""}
              onChange={(e) => setForm({ ...form, interval_months: parseInt(e.target.value || "0", 10) })}
              placeholder="ex.: 12"
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-interval-months"
            />
          </div>
          {!isPot && (<>
          <div>
            <Label className="text-slate-300">Tolerância horas (HH:MM)</Label>
            <HHMMInput
              value={form.tolerance_hours_min ?? (lifeDefault ? 0 : 600)}
              onChange={(v) => setForm({ ...form, tolerance_hours_min: v })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-tolerance-hours"
            />
          </div>
          <div>
            <Label className="text-slate-300">Tolerância meses</Label>
            <Input
              type="number" min={0} max={60}
              value={form.tolerance_months ?? ""}
              onChange={(e) => setForm({ ...form, tolerance_months: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              placeholder={lifeDefault ? "ex.: 0" : "ex.: 2"}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-tolerance-months"
            />
          </div>
          <div className="sm:col-span-2 text-[10px] text-slate-500 -mt-1">
            Margem depois do prazo em que a aeronave ainda opera, com aviso. Por defeito 10:00 h ou 2 meses (regra ANAC).
            O motor não usa esta tolerância: escolha "Potencial do motor" acima.
            {lifeDefault && tolUnset && (
              <span className="block text-signal-300 mt-0.5" data-testid="di-maint-form-life-limit-hint">
                Esta tarefa parece um limite de vida (substituição, overhaul, revisão geral): por defeito fica SEM tolerância
                (0:00 e 0 meses). Só escreva valores se a tolerância for mesmo permitida.
              </span>
            )}
          </div>
          </>)}
          {isPot && (<>
          <div className="sm:col-span-2" data-testid="di-maint-form-extension-source">
            <Label className="text-slate-300">Excedência do potencial</Label>
            <div className="mt-1 space-y-1 text-xs text-slate-300">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="ext-source" className="mt-0.5" checked={form.extension_source !== "cti"}
                  onChange={() => setForm({ ...form, extension_source: "manufacturer" })} data-testid="di-maint-form-source-manufacturer" />
                <span>O fabricante autoriza uma excedência (a Rotax diz sempre: 5 % das horas ou 6 meses, o que ocorrer primeiro).</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="ext-source" className="mt-0.5" checked={form.extension_source === "cti"}
                  onChange={() => setForm({ ...form, extension_source: "cti" })} data-testid="di-maint-form-source-cti" />
                <span>O fabricante não diz nada: aplica-se a CTI 05-04 (10 % do potencial, nas horas e no calendário, o que ocorrer primeiro).</span>
              </label>
            </div>
          </div>
          {form.extension_source !== "cti" && (<>
          <div>
            <Label className="text-slate-300">Excedência do fabricante: % das horas</Label>
            <Input
              type="number" min={0} max={50} step="0.5"
              value={form.extension_hours_pct ?? ""}
              onChange={(e) => setForm({ ...form, extension_hours_pct: e.target.value === "" ? null : parseFloat(e.target.value) })}
              placeholder="ex.: 5"
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-extension-pct"
            />
          </div>
          <div>
            <Label className="text-slate-300">Excedência do fabricante: meses</Label>
            <Input
              type="number" min={0} max={24}
              value={form.extension_months ?? ""}
              onChange={(e) => setForm({ ...form, extension_months: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              placeholder="ex.: 6"
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-extension-months"
            />
          </div>
          </>)}
          <div className="sm:col-span-2 text-[10px] text-slate-500 -mt-1">
            {form.extension_source === "cti"
              ? "10 % do potencial: 2000 h → 200 h; 15 anos → 18 meses. Vale o que ocorrer primeiro, a contar de quando o potencial foi atingido."
              : "Deixe em branco para usar os da Rotax (5 % e 6 meses, MML-912, cap. 05-10-00). Se o valor do fabricante passar o teto da CTI 05-04 (10 % do potencial), vale o teto da CTI: vale sempre o mais curto."}
          </div>
          <div>
            <Label className="text-slate-300">Data em que o potencial foi atingido (caderneta do motor)</Label>
            <Input
              type="date"
              max={todayStr()}
              value={form.potential_reached_on || ""}
              onChange={(e) => setForm({ ...form, potential_reached_on: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-reached-on"
            />
          </div>
          <div>
            <Label className="text-slate-300">Registo da caderneta (opcional)</Label>
            <Input
              value={form.potential_reached_note || ""}
              maxLength={200}
              onChange={(e) => setForm({ ...form, potential_reached_note: e.target.value })}
              placeholder="ex.: registo 5/321 (1999:55 a 23/04, 2001:25 a 24/04)"
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-reached-note"
            />
          </div>
          <div className="sm:col-span-2 text-[10px] text-slate-500 -mt-1">
            Opcional. Em branco, usa-se a data calculada pelos voos registados. Se a caderneta do motor mostra outra (o tempo de
            solo guardado no componente não tem data e pode adiantar a calculada), indique a da caderneta: é o registo legal e
            manda. Fica auditado, com o motivo.
          </div>
          </>)}
          <div>
            <Label className="text-slate-300">{isPot ? "Início do potencial: data da primeira caderneta do motor (ou da última revisão geral)" : "Data da última execução"}</Label>
            <Input
              type="date"
              max={todayStr()}
              value={form.last_done_at || ""}
              onChange={(e) => setForm({ ...form, last_done_at: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-last-date"
            />
          </div>
          <div>
            <Label className="text-slate-300">{isPot ? "Horas do motor nesse início (HH:MM; 0 se novo)" : "Horas da última execução (HH:MM)"}</Label>
            <HHMMInput
              value={form.last_done_hours_min || 0}
              onChange={(v) => setForm({ ...form, last_done_hours_min: v })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-form-last-hours"
            />
            <div className="text-[10px] text-slate-500 mt-0.5">
              Relógio atual: {fmtMin(item?.current_hours_min ?? currentHoursMin)}
            </div>
          </div>
          {criticalChanged && (
            <div className="sm:col-span-2">
              <Label className="text-slate-300">Motivo da alteração *</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="ex.: corrigido o intervalo segundo o manual"
                className="bg-slate-800 border-amber-500/40"
                data-testid="di-maint-form-reason"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Mudar prazos ou a última execução fica registado, com o seu nome e o motivo.</div>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="text-slate-300">Notas / Checklist</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-slate-800 border-slate-700 min-h-[120px] resize-y font-mono text-[12px]"
              rows={6}
              placeholder={"Lista de tarefas desta manutenção, p.ex:\n- Verificar tensão das correias\n- Limpar filtro de óleo\n- Substituir velas\n…"}
              data-testid="di-maint-form-notes"
            />
            <p className="mt-1 text-[10px] text-slate-500">Usa quebras de linha para criar um checklist do que fazer.</p>
          </div>
        </div>

        {error && <p className="mt-2 text-red-400 text-xs">{error}</p>}
    </MobileFormDialog>
  );
}


/** O que vai acontecer ao prazo seguinte, em linguagem simples (pré-visualização; o servidor decide). */
function CompletePreview({ item, doneAt, hoursAtDone }) {
  const st = item.status || {};
  if (item.rule === "potential") {
    const parts = [];
    if (hoursAtDone != null) parts.push(`às ${fmtMin(hoursAtDone)}${item.interval_hours_min ? ` → novo potencial até às ${fmtMin(hoursAtDone + item.interval_hours_min)}` : ""}`);
    if (doneAt && item.interval_months) parts.push(`por calendário até ${addMonthsStr(doneAt, item.interval_months)}`);
    return (
      <div className="rounded border border-slate-700 bg-slate-950/50 p-2" data-testid="di-maint-complete-preview">
        <p className="text-[11px] leading-snug text-slate-300">
          Registar esta execução (revisão geral ou substituição) recomeça o potencial do motor{parts.length ? `: ${parts.join("; ")}` : ""}.
        </p>
      </div>
    );
  }
  const lines = [];
  const tone = { scheduled: "text-emerald-300", first: "text-slate-300", early: "text-amber-300", late: "text-red-300" };

  const tolH = st.tolerance_hours_min ?? 600;
  const tolM = st.tolerance_months ?? 2;
  if (st.has_hours_trigger && hoursAtDone != null) {
    const plan = planAnchor(st.next_due_hours_min, hoursAtDone, tolH, false);
    const next = plan.anchor + (item.interval_hours_min || 0);
    let text;
    if (plan.why === "first") text = `Primeira execução: o prazo seguinte conta a partir de agora → próximo às ${fmtMin(next)}.`;
    else {
      const dev = hoursAtDone - st.next_due_hours_min;
      const devTxt = dev === 0 ? "no prazo" : dev > 0 ? `${fmtMin(dev)} depois do prazo` : `${fmtMin(-dev)} antes do prazo`;
      if (plan.why === "scheduled") text = `Prazo previsto às ${fmtMin(st.next_due_hours_min)} — feito ${devTxt}${tolH ? ", dentro da tolerância" : ""}. O prazo seguinte conta a partir do prazo original → próximo às ${fmtMin(next)}.`;
      else if (plan.why === "early") text = `Feito ${devTxt}${tolH ? `, fora da janela de tolerância (±${fmtMin(tolH)})` : " (este item não tem tolerância)"}. O prazo seguinte conta a partir desta execução → próximo às ${fmtMin(next)}.`;
      else text = `Feito ${devTxt}${tolH ? `: DEPOIS da tolerância (+${fmtMin(tolH)})` : ", sem tolerância"}. Fica assinalado no histórico e o prazo seguinte conta a partir desta execução → próximo às ${fmtMin(next)}.`;
    }
    lines.push({ key: "h", tone: tone[plan.why], text });
  }
  if (st.has_calendar_trigger && doneAt) {
    const plan = planAnchor(st.next_due_date, doneAt, tolM, true);
    const next = addMonthsStr(plan.anchor, item.interval_months || 0);
    let text;
    if (plan.why === "first") text = `Primeira execução: o prazo seguinte conta a partir desta data → próximo em ${next}.`;
    else if (plan.why === "scheduled") text = `Prazo previsto em ${fmtDate(st.next_due_date)}${tolM ? ", dentro da tolerância" : ""}. O prazo seguinte conta a partir do prazo original → próximo em ${next}.`;
    else if (plan.why === "early") text = `Feito antes de ${fmtDate(st.next_due_date)}${tolM ? ` (fora da janela de ${tolM} meses)` : " (este item não tem tolerância)"}. O prazo seguinte conta a partir desta data → próximo em ${next}.`;
    else text = `Feito DEPOIS do prazo (${fmtDate(st.next_due_date)}${tolM ? ` + ${tolM} meses de tolerância` : ", sem tolerância"}). Fica assinalado e o prazo seguinte conta a partir desta data → próximo em ${next}.`;
    lines.push({ key: "c", tone: tone[plan.why], text });
  }
  if (!lines.length) return null;
  return (
    <div className="rounded border border-slate-700 bg-slate-950/50 p-2 space-y-1" data-testid="di-maint-complete-preview">
      {lines.map((l) => <p key={l.key} className={`text-[11px] leading-snug ${l.tone}`}>{l.text}</p>)}
    </div>
  );
}


function CompleteForm({ item, items, currentHoursMin, onClose, onSaved }) {
  // O relógio certo é o do próprio item (componente ou motor); o do cabeçalho serve de reserva
  const itemHours = item?.current_hours_min ?? currentHoursMin ?? 0;
  const [form, setForm] = useState(() => ({
    done_at: todayStr(),
    hours_min_at_done: itemHours,   // leitura real do conta-horas
    performed_by: "",
    notes: "",
  }));
  const candidates = includedCandidates(item, items);
  // Pré-marcadas só as inspeções (o resto fica por marcar à mão): nada se fecha sem o utilizador ver
  const [included, setIncluded] = useState(() => new Set(candidates.filter(isInspection).map((o) => o.id)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const toggleIncluded = (id) => setIncluded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    setError("");
    if (!form.done_at) {
      setError("Data é obrigatória.");
      return;
    }
    if (form.done_at > todayStr()) {
      setError("A data da execução não pode estar no futuro.");
      return;
    }
    if (form.hours_min_at_done == null) {
      setError("Indique a leitura do conta-horas (HH:MM).");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/di/maintenance/${item.id}/complete`,
        { ...form, also_complete: candidates.filter((o) => included.has(o.id)).map((o) => o.id) },
        { withCredentials: true },
      );
      const done = r.data?.included || [];
      toast.success(done.length ? `Manutenção registada. Também fechadas: ${done.map((i) => i.task).join(", ")}` : "Manutenção registada");
      (r.data?.skipped || []).forEach((x) => toast.warning(`${x.task || "Tarefa"} não foi incluída: ${x.reason}.`, { duration: 12000 }));
      (r.data?.warnings || []).forEach((w) => toast.warning(w, { duration: 12000 }));
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erro");
    } finally {
      setSaving(false);
    }
  };

  const aboveRecorded = form.hours_min_at_done != null && form.hours_min_at_done > itemHours;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-md max-h-[90vh] overflow-y-auto" data-testid="di-maint-complete-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Registar execução</DialogTitle>
          <DialogDescription className="text-slate-400">
            {item.component} · {item.task}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-xs">
          <div>
            <Label className="text-slate-300">Data em que foi feita *</Label>
            <Input
              type="date"
              max={todayStr()}
              value={form.done_at}
              onChange={(e) => setForm({ ...form, done_at: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-complete-date"
            />
          </div>

          <div>
            <Label className="text-slate-300">Leitura do conta-horas na altura (HH:MM) *</Label>
            <HHMMInput
              value={form.hours_min_at_done}
              onChange={(v) => setForm({ ...form, hours_min_at_done: v })}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-complete-hours"
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Horas registadas agora: {fmtMin(itemHours)}. O sistema já sabe o prazo previsto e calcula sozinho o seguinte.
            </p>
            {aboveRecorded && (
              <p className="mt-1 text-[11px] text-amber-300" data-testid="di-maint-complete-above">
                A leitura é superior às horas registadas ({fmtMin(itemHours)}): faltarão voos ou tempo de solo por registar?
              </p>
            )}
          </div>

          <CompletePreview item={item} doneAt={form.done_at} hoursAtDone={form.hours_min_at_done} />

          {candidates.length > 0 && (
            <div className="rounded border border-slate-700 bg-slate-950/40 p-2 space-y-1" data-testid="di-maint-included">
              <div className="text-[11px] text-slate-200 font-semibold">Tarefas incluídas nesta execução</div>
              <p className="text-[10px] text-slate-500">
                As tarefas mais pequenas do mesmo componente que esta execução cobre ficam registadas na mesma leitura, cada uma
                com o seu prazo (a de 100 h inclui as de 25 h e 50 h). Desmarque as que não foram feitas.
              </p>
              {candidates.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-[11px] text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={included.has(o.id)}
                    onChange={() => toggleIncluded(o.id)}
                    className="h-3.5 w-3.5 accent-emerald-400"
                    data-testid={`di-maint-included-${o.id}`}
                  />
                  <span>{o.task}</span>
                  <span className="text-slate-500">· a cada {fmtMin(o.interval_hours_min)}</span>
                </label>
              ))}
            </div>
          )}

          <div>
            <Label className="text-slate-300">Realizado por</Label>
            <Input
              value={form.performed_by}
              onChange={(e) => setForm({ ...form, performed_by: e.target.value })}
              placeholder="Oficina / técnico"
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-complete-by"
            />
          </div>
          <div>
            <Label className="text-slate-300">Notas</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="bg-slate-800 border-slate-700"
              data-testid="di-maint-complete-notes"
            />
          </div>
        </div>

        {error && <p className="mt-2 text-red-400 text-xs">{error}</p>}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="di-maint-complete-submit">
            {saving ? "A guardar…" : "Registar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function HistoryDialog({ item, onClose, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, a] = await Promise.all([
        axios.get(`${API}/di/maintenance/${item.id}/history`, { withCredentials: true }),
        axios.get(`${API}/di/maintenance/${item.id}/audit`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setEntries(h.data || []);
      setAudit(a.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setLoading(false);
    }
  }, [item.id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (hid, reason) => {
    try {
      await axios.delete(`${API}/di/maintenance/${item.id}/history/${hid}`, { withCredentials: true, params: { reason } });
      toast.success("Entrada removida (fica na auditoria)");
      setRemoving(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Erro");
    }
  };

  const ACTION_PT = { create: "Criado", update: "Alterado", delete: "Eliminado", complete: "Execução registada", history_delete: "Execução apagada" };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="di-maint-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><History className="w-5 h-5" /> Histórico</DialogTitle>
          <DialogDescription className="text-slate-400">
            {item.component} · {item.task}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-slate-400 text-sm">A carregar…</div>
        ) : entries.length === 0 ? (
          <div className="text-slate-500 text-sm italic">Sem execuções registadas.</div>
        ) : (
          <div className="space-y-2" data-testid="di-maint-history-list">
            {entries.map((e) => {
              const noteLines = (e.notes || "").split("\n");
              const devParts = [];
              if (e.date_deviation_days != null && e.date_deviation_days !== 0) devParts.push(`${e.date_deviation_days > 0 ? "+" : ""}${e.date_deviation_days}d`);
              if (e.hours_deviation_min != null && e.hours_deviation_min !== 0) devParts.push(`${e.hours_deviation_min > 0 ? "+" : "-"}${fmtMin(Math.abs(e.hours_deviation_min))}`);
              return (
                <div
                  key={e.id}
                  className="border border-slate-700 rounded p-2.5 bg-slate-900/40"
                  data-testid="di-maint-history-row"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[11px] space-y-0.5 flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-emerald-300">{e.done_at}</span>
                        <span className="text-slate-500">·</span>
                        <span className="font-mono text-amber-300">{fmtMin(e.hours_min_at_done)}</span>
                        {e.performed_by && (
                          <>
                            <span className="text-slate-500">·</span>
                            <span className="text-slate-300">{e.performed_by}</span>
                          </>
                        )}
                        {e.late_beyond_tolerance && (
                          <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 text-[10px] font-bold uppercase" title="Feita depois do limite de tolerância">Depois da tolerância</span>
                        )}
                        {e.early_beyond_window && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px] font-bold uppercase" title="Feita muito antes do prazo">Muito antecipada</span>
                        )}
                      </div>
                      {(e.scheduled_due_date || e.scheduled_due_hours_min != null) && (
                        <div className="text-[10px] text-slate-500" data-testid="di-maint-history-plan">
                          Previsto: {[e.scheduled_due_date ? fmtDate(e.scheduled_due_date) : null, e.scheduled_due_hours_min != null ? fmtMin(e.scheduled_due_hours_min) : null].filter(Boolean).join(" · ")}
                          {devParts.length > 0 && <span> — desvio {devParts.join(" / ")}</span>}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setRemoving(e)}
                      className="p-1 rounded hover:bg-slate-800 text-red-400 hover:text-red-300 shrink-0"
                      title="Corrigir: eliminar esta entrada (pede motivo)"
                      data-testid="di-maint-history-delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {e.notes && (
                    <pre className="mt-1.5 whitespace-pre-wrap font-mono text-[11px] text-slate-300 bg-slate-950/50 border border-slate-800 rounded p-2" data-testid="di-maint-history-notes">
                      {e.notes}
                    </pre>
                  )}
                  {noteLines.length === 0 && !e.notes && (
                    <div className="mt-1 text-[10px] text-slate-500 italic">— sem notas —</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {audit.length > 0 && (
          <div className="mt-4" data-testid="di-maint-audit">
            <h6 className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Alterações e correções</h6>
            <ul className="space-y-1">
              {audit.map((a) => (
                <li key={a.id} className="text-[11px] text-slate-400 border-l-2 border-slate-700 pl-2">
                  <span className="text-slate-300">{ACTION_PT[a.action] || a.action}</span>
                  {" · "}{(a.ts || "").slice(0, 16).replace("T", " ")}
                  {a.actor?.email ? ` · ${a.actor.email}` : ""}
                  {a.reason ? <span className="text-amber-300"> — {a.reason}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Fechar</Button>
        </DialogFooter>

        {removing && (
          <ReasonDialog
            title="Eliminar esta execução?"
            description={`${removing.done_at} · ${fmtMin(removing.hours_min_at_done)} — a última execução do item é recalculada a partir das restantes. A entrada fica guardada na auditoria.`}
            confirmLabel="Eliminar"
            onCancel={() => setRemoving(null)}
            onConfirm={(reason) => remove(removing.id, reason)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
